import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {
  analyticsDashboardGlobalScreenState,
  isBlockEditorScreenState,
} from 'insights/analytics-dashboards/details/view-state/ScreenState';
import {FormType} from 'insights/analytics-queries/block-editor/sql-template-form/SQLTemplateFormTypes';
import {getAnalyticsQuerySQLTemplateState} from 'insights/analytics-queries/block-editor/sql-template-form/SQLTemplateFormUtil';
import cellToBlobs from 'insights/analytics-visualizations/blobs/cellToBlobs';
import {cellToChipItemIds} from 'insights/analytics-visualizations/chip-items/ChipItems';
import {convertGQLToRESTAnalyticsVisualization} from 'insights/analytics-visualizations/data/AnalyticsVisualizationGQLRESTConverters';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import getParameterNamesInSQLQueries from 'insights/parameters/data/getParameterNamesInSQLQueries';
import isDefaultSystemName from 'insights/parameters/data/isDefaultSystemName';
import {DEFAULT_SQL_QUERY, NO_RESULT_SQL_QUERY} from 'insights/warehouse/AnalyticsWarehouseConstants';
import {LegacyRunQueryOutput, LegacyRunQueryState, Row} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

const KEYWORDS = [
  'assay_result_schema',
  'assay_run_schema',
  'author',
  'batch',
  'batch_schema',
  'box',
  'box_schema',
  'container',
  'container_content',
  'container_schema',
  'container_transfer',
  'entity_schema',
  'entry',
  'entry_auditor',
  'field',
  'field_definition',
  'folder',
  'location',
  'location_schema',
  'plate',
  'plate_schema',
  'project',
  'registry_entity',
  'request_assignee',
  'request_fulfillment',
  'request_sample',
  'request_schema',
  'schema',
  'stage_run',
  'team',
  'team_member',
  'user',
  'workflow',
  'workflow_template',
  'workflow_template_version',
];
const KEYWORDS_REGEX = KEYWORDS.join('|');

function containsKeywords(sqlQuery: string): boolean {
  return Boolean(sqlQuery.match(KEYWORDS_REGEX));
}

function containsRaw(sqlQuery: string): boolean {
  return sqlQuery.includes('$raw');
}

function getSerializedByteLength(data: Array<Row>): number {
  return new Blob([JSON.stringify(data)]).size;
}

function getAllChips(data: LegacyRunQueryOutput): Array<string> {
  return data.rows.flatMap((row) => row.flatMap((cell) => cellToChipItemIds(cell) ?? []));
}

function countAllChips(data: LegacyRunQueryOutput): number {
  return getAllChips(data).length;
}

function countUniqueChips(data: LegacyRunQueryOutput): number {
  return new Set(getAllChips(data)).size;
}

function getAllBlobs(data: LegacyRunQueryOutput): Array<BenchlingBlob> {
  return data.rows.flatMap((row) => row.flatMap((cell) => cellToBlobs(cell) ?? []));
}

function countAllBlobs(data: LegacyRunQueryOutput): number {
  return getAllBlobs(data).length;
}

function countUniqueBlobs(data: LegacyRunQueryOutput): number {
  return new Set(getAllBlobs(data).map((blob) => blob.id)).size;
}

export interface ExtraDataToTrackRunQuery {
  alreadyFetched: boolean;
  sqlTemplateFormType: FormType | null;
}

const FETCHED_STATUSES = [AsyncStatus.SUCCESS, AsyncStatus.ERROR];

export default function trackRunQuery(
  analyticsQuery: AnalyticsQuery,
  runQueryState: LegacyRunQueryState,
  extraData: ExtraDataToTrackRunQuery,
  sqlQueryOverride?: string
): void {
  if (!FETCHED_STATUSES.includes(runQueryState.status)) {
    return;
  }
  const query = sqlQueryOverride != null ? sqlQueryOverride : analyticsQuery.sqlQuery;
  const parameterNames = getParameterNamesInSQLQueries([query]);
  const analyticsEvent = extraData.alreadyFetched
    ? INSIGHTS_ANALYTICS_ACTIONS.RAN_QUERY
    : INSIGHTS_ANALYTICS_ACTIONS.LOADED_QUERY;
  deprecatedTrackEvent(analyticsEvent, {
    block_id: analyticsQuery.id,
    contains_keywords: containsKeywords(query),
    contains_raw: containsRaw(query),
    data_size_bytes:
      runQueryState.status === AsyncStatus.SUCCESS ? getSerializedByteLength(runQueryState.data.rows) : null,
    has_error: runQueryState.status === AsyncStatus.ERROR,
    is_default: query === DEFAULT_SQL_QUERY || query === NO_RESULT_SQL_QUERY,
    is_truncated: runQueryState.status === AsyncStatus.SUCCESS ? runQueryState.data.isTruncated : null,
    num_blobs: runQueryState.status === AsyncStatus.SUCCESS ? countAllBlobs(runQueryState.data) : null,
    num_chips: runQueryState.status === AsyncStatus.SUCCESS ? countAllChips(runQueryState.data) : null,
    num_columns: runQueryState.status === AsyncStatus.SUCCESS ? runQueryState.data.headers.length : null,
    num_rows: runQueryState.status === AsyncStatus.SUCCESS ? runQueryState.data.rows.length : null,
    num_non_default_parameters: parameterNames.filter((name) => !isDefaultSystemName(name)).length,
    num_parameters: parameterNames.length,
    num_unique_blobs:
      runQueryState.status === AsyncStatus.SUCCESS ? countUniqueBlobs(runQueryState.data) : null,
    num_unique_chips:
      runQueryState.status === AsyncStatus.SUCCESS ? countUniqueChips(runQueryState.data) : null,
    query_created_at: analyticsQuery.createdAt,
    query_creator_api_id: analyticsQuery.creator.id,
    query_modified_at: analyticsQuery.modifiedAt,
    sql_template_form_type: extraData.sqlTemplateFormType,
    visualization_configuration: convertGQLToRESTAnalyticsVisualization(analyticsQuery.visualization)
      .parameters,
    visualization_id: analyticsQuery.visualization.id,
    visualization_type: getVisualizationType(analyticsQuery.visualization),
  });
}

export function getExtraDataToTrackRunQuery(
  analyticsDashboardViewStateId: string,
  alreadyFetched: boolean
): ExtraDataToTrackRunQuery {
  const screenState = analyticsDashboardGlobalScreenState.getState(analyticsDashboardViewStateId);

  const openedAnalyticsQuery = isBlockEditorScreenState(screenState) ? screenState.analyticsQuery : null;

  const sqlTemplateFormState = openedAnalyticsQuery
    ? getAnalyticsQuerySQLTemplateState(openedAnalyticsQuery)
    : null;

  // This is currently the dashboard's createdAt, creator, and modifiedAt, but should ideally be changed to
  // the block's because AnalyticsQuery tracks them.
  return {
    alreadyFetched,
    sqlTemplateFormType: sqlTemplateFormState ? sqlTemplateFormState.formType : null,
  };
}
